/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import {
	Close as CloseIcon,
	Security as ShieldCheck,
	People as Users,
	PersonAdd,
	GroupAdd,
	Badge as IdCard,
	Verified,
	CreditCard,
	CreditScore,
	EditNote,
	Dashboard,
	Schedule as CalendarClock,
	Savings as HandCoins,
	FormatListNumbered as ListOrdered,
	Receipt,
	NotificationsActive as BellRing,
	Timeline,
	ChevronLeft,
	ChevronRight,
	AutoAwesome,
} from "@mui/icons-material";
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	IconButton,
	LinearProgress,
	Link,
	Typography,
	Modal,
	useTheme,
	useMediaQuery, alpha,
} from "@mui/material";
import { colors } from "../../utility/types/constant";

/**
 * FundsyFi HowItWorks Modal
 * - Mobile-first (420x640 style)
 * - Animated step transitions
 * - Progress dots
 * - MUI components
 */

export type HowItWorksModalProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Optional: fire when user clicks the final CTA */
	onGetStarted?: () => void;
};

type Step = {
	title: string;
	body: React.ReactNode;
	icon: React.ElementType;
	illustrationLabel: string;
	/** Optional: provide a URL/imported image to show instead of the icon in the illustration panel */
	image?: string;
	/** Optional: stretch the image to fill the full illustration panel instead of icon-sized */
	imageWide?: boolean;
	/** Optional: short badge label shown above the title */
	badge?: string;
};

function clamp(n: number, min: number, max: number) {
	return Math.max(min, Math.min(max, n));
}

export default function HowItWorksModal({
	open,
	onOpenChange,
	onGetStarted,
}: HowItWorksModalProps) {
	const theme = useTheme();
	const { t } = useTranslation("modals");
	const stepVariants = {
		initial: (direction: number) => ({ opacity: 0, x: direction > 0 ? 24 : -24 }),
		animate: { opacity: 1, x: 0 },
		exit: (direction: number) => ({ opacity: 0, x: direction > 0 ? -24 : 24 }),
	};

	const steps: Step[] = useMemo(
		() => [
			/* Overview */
			{
				title: t("howItWorks.steps.overview.title"),
				body: t("howItWorks.steps.overview.body"),
				icon: Users,
				illustrationLabel: t("howItWorks.steps.overview.label"),
				badge: t("howItWorks.steps.overview.badge"),
				image: "/images/how-it-works-images/primary-logo-1-1.png",
			},
			/* Create Account */
			{
				title: t("howItWorks.steps.createAccount.title"),
				body: t("howItWorks.steps.createAccount.body"),
				icon: PersonAdd,
				illustrationLabel: t("howItWorks.steps.createAccount.label"),
				badge: t("howItWorks.steps.createAccount.badge"),
				image: "/images/how-it-works-images/sign-uo-1.png",
				imageWide: true,
			},
			/* Join Circle */
			{
				title: t("howItWorks.steps.joinCircle.title"),
				body: t("howItWorks.steps.joinCircle.body"),
				icon: PersonAdd,
				illustrationLabel: t("howItWorks.steps.joinCircle.label"),
				badge: t("howItWorks.steps.joinCircle.badge"),
				image: "/images/how-it-works-images/join-circle-request.png",
				imageWide: true,
			},
			/* Create Circle */
			{
				title: t("howItWorks.steps.createCircle.title"),
				body: t("howItWorks.steps.createCircle.body"),
				icon: GroupAdd,
				illustrationLabel: t("howItWorks.steps.createCircle.label"),
				badge: t("howItWorks.steps.createCircle.badge"),
				image: "/images/how-it-works-images/create-circle.png",
				imageWide: true,
			},
			/* Verify Identity */
			{
				title: t("howItWorks.steps.verifyIdentity.title"),
				body: t("howItWorks.steps.verifyIdentity.body"),
				icon: IdCard,
				illustrationLabel: t("howItWorks.steps.verifyIdentity.label"),
				badge: t("howItWorks.steps.verifyIdentity.badge"),
				image: "/images/how-it-works-images/identity-verification-2.png",
				imageWide: true,
			},
			/* Stripe Check */
			{
				title: t("howItWorks.steps.stripeCheck.title"),
				body: t("howItWorks.steps.stripeCheck.body"),
				icon: Verified,
				illustrationLabel: t("howItWorks.steps.stripeCheck.label"),
				badge: t("howItWorks.steps.stripeCheck.badge"),
				image: "/images/how-it-works-images/identity-verification.png",
				imageWide: true,
			},
			/* Payout Method */
			{
				title: t("howItWorks.steps.payoutMethod.title"),
				body: t("howItWorks.steps.payoutMethod.body"),
				icon: CreditCard,
				illustrationLabel: t("howItWorks.steps.payoutMethod.label"),
				badge: t("howItWorks.steps.payoutMethod.badge"),
				image: "/images/how-it-works-images/payout-act-setup1.png",
				imageWide: true,
			},
			/* Complete Profile */
			{
				title: t("howItWorks.steps.completeProfile.title"),
				body: t("howItWorks.steps.completeProfile.body"),
				icon: EditNote,
				illustrationLabel: t("howItWorks.steps.completeProfile.label"),
				badge: t("howItWorks.steps.completeProfile.badge"),
			},
			/* All Set */
			{
				title: t("howItWorks.steps.allSet.title"),
				body: t("howItWorks.steps.allSet.body"),
				icon: Dashboard,
				illustrationLabel: t("howItWorks.steps.allSet.label"),
				badge: t("howItWorks.steps.allSet.badge"),
				image: "/images/how-it-works-images/member-dashboard.png",
				imageWide: true,
			},
			/* Never Miss */
			{
				title: t("howItWorks.steps.neverMiss.title"),
				body: t("howItWorks.steps.neverMiss.body"),
				icon: CreditScore,
				illustrationLabel: t("howItWorks.steps.neverMiss.label"),
				badge: t("howItWorks.steps.neverMiss.badge"),
				image: "/images/how-it-works-images/add-card.png",
				imageWide: true,
			},
			/* Contributions */
			{
				title: t("howItWorks.steps.contributions.title"),
				body: t("howItWorks.steps.contributions.body"),
				icon: CalendarClock,
				illustrationLabel: t("howItWorks.steps.contributions.label"),
				badge: t("howItWorks.steps.contributions.badge"),
				image: "/images/how-it-works-images/contribute.png",
				imageWide: true,
			},
			/* Payout Disbursed */
			{
				title: t("howItWorks.steps.payoutDisbursed.title"),
				body: t("howItWorks.steps.payoutDisbursed.body"),
				icon: HandCoins,
				illustrationLabel: t("howItWorks.steps.payoutDisbursed.label"),
				badge: t("howItWorks.steps.payoutDisbursed.badge"),
				image: "/images/how-it-works-images/payout.png",
				imageWide: true,
			},
			/* Payout Transparent */
			{
				title: t("howItWorks.steps.payoutTransparent.title"),
				body: (
					<Trans
						i18nKey="howItWorks.steps.payoutTransparent.body"
						ns="modals"
						components={{ strong: <strong /> }}
					/>
				),
				icon: ListOrdered,
				illustrationLabel: t("howItWorks.steps.payoutTransparent.label"),
				badge: t("howItWorks.steps.payoutTransparent.badge"),
			},
			/* Fees */
			{
				title: t("howItWorks.steps.fees.title"),
				body: "",
				icon: Receipt,
				illustrationLabel: t("howItWorks.steps.fees.label"),
				badge: t("howItWorks.steps.fees.badge"),
			},
			/* Late Payments */
			{
				title: t("howItWorks.steps.latePayments.title"),
				body: "",
				icon: BellRing,
				illustrationLabel: t("howItWorks.steps.latePayments.label"),
				badge: t("howItWorks.steps.latePayments.badge"),
				image: "/images/how-it-works-images/missed-contribution-notice.png",
				imageWide: true,
			},
			/* Track */
			{
				title: t("howItWorks.steps.track.title"),
				body: t("howItWorks.steps.track.body"),
				icon: Timeline,
				illustrationLabel: t("howItWorks.steps.track.label"),
				badge: t("howItWorks.steps.track.badge"),
				image: "/images/how-it-works-images/tracking.png",
				imageWide: true,
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t],
	);

	const total = steps.length;
	const [index, setIndex] = useState(0);
	const [direction, setDirection] = useState(1);

	// reset to step 1 whenever opened
	useEffect(() => {
		if (open) {
			setIndex(0);
			setDirection(1);
		}
	}, [open]);

	// ESC to close, arrow keys to navigate
	useEffect(() => {
		if (!open) return;
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") onOpenChange(false);
			if (e.key === "ArrowRight") handleNext();
			if (e.key === "ArrowLeft") handlePrev();
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, index]);

	const isLast = index === total - 1;
	const isFirst = index === 0;

	function goTo(nextIndex: number) {
		const target = clamp(nextIndex, 0, total - 1);
		if (target === index) return;
		setDirection(target > index ? 1 : -1);
		setIndex(target);
	}

	function handleNext() {
		if (isLast) {
			onOpenChange(false);
			onGetStarted?.();
			return;
		}
		goTo(index + 1);
	}

	function handlePrev() {
		if (isFirst) return;
		goTo(index - 1);
	}

	const step = steps[index];
	const Icon = step.icon;

	return (
		<Modal
			open={open}
			onClose={() => onOpenChange(false)}
			aria-labelledby="how-it-works-modal"
			sx={{
				display: "flex",
				alignItems: { xs: "flex-end", sm: "center" },
				justifyContent: "center",
				p: { xs: 0, sm: 2, md: 3 },
			}}>
			<Box
				sx={{
					width: "100%",
					maxWidth: 1000,
					display: "flex",
					justifyContent: "center",
					outline: "none",
				}}>
				<AnimatePresence mode="wait" initial={false}>
					{open && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
							style={{ width: "100%", outline: "none" }}>
							<motion.div
								initial={{ opacity: 0, y: 16, scale: 0.98 }}
								animate={{ opacity: 1, y: 0, scale: 1 }}
								exit={{ opacity: 0, y: 16, scale: 0.98 }}
								transition={{ type: "spring", stiffness: 340, damping: 30 }}>
								<Card
									sx={{
										borderRadius: { xs: "20px 20px 0 0", sm: 3, md: 4 },
										overflow: "hidden",
										boxShadow: "0 24px 80px rgba(4,13,69,0.22)",
										border: `1px solid ${alpha(colors.mainSecondary, 0.12)}`,
										maxHeight: { xs: "93dvh", sm: "90dvh", md: "none" },
										display: "flex",
										flexDirection: "column",
									}}>
									<CardContent sx={{ p: 0, flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", "&:last-child": { pb: 0 } }}>

										{/* ── Top bar ── */}
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												px: { xs: 2.5, md: 4 },
												py: 2,
												background: `linear-gradient(135deg, ${colors.primary} 0%, #1a2a8a 100%)`,
											}}>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
												<Box
													sx={{
														height: 36,
														width: 36,
														borderRadius: 2,
														backgroundColor: alpha(colors.mainSecondary, 0.18),
														border: `1px solid ${alpha(colors.mainSecondary, 0.35)}`,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
													}}>
													<ShieldCheck sx={{ fontSize: 20, color: colors.mainSecondary }} />
												</Box>
												<Box>
													<Typography
														variant="subtitle1"
														fontWeight={700}
														sx={{ color: "#fff", fontFamily: "'Montserrat', sans-serif", lineHeight: 1.2 }}>
														{t("howItWorks.title")}
													</Typography>
													<Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
														{t("howItWorks.stepCounter", { current: index + 1, total, label: step.illustrationLabel })}
													</Typography>
												</Box>
											</Box>

											<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
												<Chip
													icon={<AutoAwesome sx={{ fontSize: "13px !important", color: `${colors.mainSecondary} !important` }} />}
													label={t("howItWorks.quickWalkthrough")}
													size="small"
													sx={{
														display: { xs: "none", sm: "flex" },
														backgroundColor: alpha(colors.mainSecondary, 0.14),
														color: colors.mainSecondary,
														fontFamily: "'Montserrat', sans-serif",
														fontWeight: 600,
														fontSize: "0.7rem",
														border: `1px solid ${alpha(colors.mainSecondary, 0.3)}`,
														"& .MuiChip-label": { px: 1 },
													}}
												/>
												<IconButton
													onClick={() => onOpenChange(false)}
													size="small"
													sx={{
														color: "rgba(255,255,255,0.7)",
														border: "1px solid rgba(255,255,255,0.18)",
														borderRadius: 2,
														"&:hover": {
															color: "#fff",
															backgroundColor: "rgba(255,255,255,0.1)",
														},
													}}>
													<CloseIcon fontSize="small" />
												</IconButton>
											</Box>
										</Box>

										{/* ── Progress bar ── */}
										<LinearProgress
											variant="determinate"
											value={((index + 1) / total) * 100}
											sx={{
												height: 3,
												backgroundColor: alpha(colors.mainSecondary, 0.12),
												"& .MuiLinearProgress-bar": {
													backgroundColor: colors.mainSecondary,
													transition: "transform 0.4s ease",
												},
											}}
										/>

										{/* ── Two-column body ── */}
										<Box
											sx={{
												display: "grid",
												gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
												minHeight: { xs: "auto", md: 520 },
											}}>

											{/* ── Left: Illustration panel ── */}
											<AnimatePresence mode="popLayout" custom={direction} initial={false}>
												<motion.div
													key={`illustration-${index}`}
													custom={direction}
													variants={stepVariants}
													initial="initial"
													animate="animate"
													exit="exit"
													transition={{ type: "spring", stiffness: 380, damping: 32 }}
													style={{ display: "flex" }}>
													<Box
														sx={{
															position: "relative",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															background: `linear-gradient(160deg, ${alpha(colors.primary, 0.04)} 0%, ${alpha(colors.mainSecondary, 0.07)} 100%)`,
															borderRight: { xs: "none", md: `1px solid ${theme.palette.divider}` },
															borderBottom: { xs: `1px solid ${theme.palette.divider}`, md: "none" },
															minHeight: { xs: 320, md: "auto" },
															width: "100%",
															p: step.imageWide ? 0 : 4,
															overflow: "hidden",
														}}>
														{/* Background circle decoration */}
														<Box
															sx={{
																position: "absolute",
																width: 320,
																height: 320,
																borderRadius: "50%",
																background: `radial-gradient(circle, ${alpha(colors.mainSecondary, 0.08)} 0%, transparent 70%)`,
																top: "50%",
																left: "50%",
																transform: "translate(-50%, -50%)",
																pointerEvents: "none",
															}}
														/>

														{/* Image OR animated icon */}
														{step.image ? (
															step.imageWide ? (
																<Box
																	component="img"
																	src={step.image}
																	alt={step.illustrationLabel}
																	sx={{
																		width: "100%",
																		height: "auto",
																		maxHeight: { xs: 260, sm: 340, md: "none" },
																		display: "block",
																		objectFit: "contain",
																		position: "relative",
																		zIndex: 1,
																	}}
																/>
															) : (
																<Box
																	component="img"
																	src={step.image}
																	alt={step.illustrationLabel}
																	sx={{
																		width: 120,
																		height: 120,
																		objectFit: "contain",
																		borderRadius: "50%",
																		position: "relative",
																		zIndex: 1,
																		boxShadow: `0 16px 48px ${alpha(colors.mainSecondary, 0.35)}`,
																	}}
																/>
															)
														) : (
															<motion.div
																animate={{ y: [0, -8, 0] }}
																transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
																style={{ position: "relative", zIndex: 1 }}>
																<Box
																	sx={{
																		width: 120,
																		height: 120,
																		borderRadius: "50%",
																		background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, #17a882 100%)`,
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "center",
																		boxShadow: `0 16px 48px ${alpha(colors.mainSecondary, 0.35)}`,
																	}}>
																	<Icon sx={{ fontSize: 56, color: "#fff" }} />
																</Box>
															</motion.div>
														)}

														{/* Label chip */}
														<Box
															sx={{
																position: "absolute",
																bottom: 16,
																left: 16,
																zIndex: 2,
																display: "flex",
																alignItems: "center",
																gap: 1,
																borderRadius: 10,
																border: `1px solid ${theme.palette.divider}`,
																bgcolor: alpha(theme.palette.background.paper, 0.9),
																px: 1.5,
																py: 0.5,
																backdropFilter: "blur(8px)",
															}}>
															<Box
																sx={{
																	height: 22,
																	width: 22,
																	borderRadius: "50%",
																	bgcolor: alpha(colors.mainSecondary, 0.12),
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																}}>
																<Icon sx={{ fontSize: 13, color: colors.mainSecondary }} />
															</Box>
															<Typography variant="caption" color="text.secondary" fontWeight={500}>
																{step.illustrationLabel}
															</Typography>
														</Box>
													</Box>
												</motion.div>
											</AnimatePresence>

											{/* ── Right: Content panel ── */}
											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													p: { xs: 2.5, sm: 3, md: 4.5 },
													backgroundColor: theme.palette.background.paper,
												}}>
												{/* Progress dots */}
												<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: { xs: 2, md: 3 }, overflow: "hidden" }}>
													{steps.map((_, i) => {
														const active = i === index;
														const done = i < index;
														return (
															<Box
																key={i}
																component="button"
																onClick={() => goTo(i)}
																title={`Go to step ${i + 1}`}
																sx={{
																	p: 0,
																	border: "none",
																	background: "none",
																	cursor: "pointer",
																	display: "flex",
																	alignItems: "center",
																}}>
																<Box
																	sx={{
																		height: active ? 8 : 6,
																		width: active ? 24 : 6,
																		borderRadius: "50px",
																		bgcolor: colors.mainSecondary,
																		opacity: active ? 1 : done ? 0.55 : 0.2,
																		transition: "all 0.3s ease",
																	}}
																/>
															</Box>
														);
													})}
													<Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
														{index + 1} / {total}
													</Typography>
												</Box>

												{/* Step content */}
												<Box sx={{ flex: 1 }}>
													<AnimatePresence mode="popLayout" custom={direction} initial={false}>
														<motion.div
															key={`content-${index}`}
															custom={direction}
															variants={stepVariants}
															initial="initial"
															animate="animate"
															exit="exit"
															transition={{ type: "spring", stiffness: 380, damping: 32 }}>

															{step.badge && (
																<Chip
																	label={step.badge}
																	size="small"
																	sx={{
																		mb: 1.5,
																		backgroundColor: alpha(colors.mainSecondary, 0.1),
																		color: colors.mainSecondary,
																		fontFamily: "'Montserrat', sans-serif",
																		fontWeight: 700,
																		fontSize: "0.7rem",
																		letterSpacing: "0.06em",
																		border: `1px solid ${alpha(colors.mainSecondary, 0.25)}`,
																	}}
																/>
															)}

															<Typography
																variant="h5"
																fontWeight={800}
																gutterBottom
																sx={{
																	fontFamily: "'Montserrat', sans-serif",
																	color: colors.primary,
																	letterSpacing: "-0.02em",
																	lineHeight: 1.25,
																	fontSize: { xs: "1.3rem", md: "1.5rem" },
																}}>
																{step.title}
															</Typography>

															<Box
																sx={{
																	width: 40,
																	height: 3,
																	borderRadius: 2,
																	backgroundColor: colors.mainSecondary,
																	mb: 2,
																}}
															/>

															<Typography
																variant="body1"
																color="text.secondary"
																sx={{
																	lineHeight: 1.75,
																	fontFamily: "'Montserrat', sans-serif",
																	fontSize: "0.9375rem",
																}}>
																{step.body}
															</Typography>

															{/* Contextual helper messages */}
															{index === 3 && (
																<Box
																	sx={{
																		mt: 2.5,
																		p: 2,
																		borderRadius: 2,
																		border: `1px solid ${alpha(colors.mainSecondary, 0.2)}`,
																		bgcolor: alpha(colors.mainSecondary, 0.05),
																	}}>
																	<Typography variant="caption" color="text.secondary">
																		<Trans
																			i18nKey="howItWorks.hints.verificationNote"
																			ns="modals"
																			components={{ strong: <strong /> }}
																		/>
																	</Typography>
																</Box>
															)}
															{index === 13 && (
																<Box
																	sx={{
																		mt: 2.5,
																		p: 2,
																		borderRadius: 2,
																		border: `1px solid ${alpha(colors.mainSecondary, 0.2)}`,
																		bgcolor: alpha(colors.mainSecondary, 0.05),
																	}}>
																	<Typography variant="caption" color="text.secondary">
																		{t("howItWorks.hints.feesNote")}
																	</Typography>
																</Box>
															)}
															{index === 14 && (
																<Box
																	sx={{
																		mt: 2.5,
																		p: 2,
																		borderRadius: 2,
																		border: `1px solid ${alpha(colors.mainSecondary, 0.2)}`,
																		bgcolor: alpha(colors.mainSecondary, 0.05),
																	}}>
																	<Typography variant="caption" color="text.secondary">
																		{t("howItWorks.hints.lateFeesNote")}
																	</Typography>
																</Box>
															)}
														</motion.div>
													</AnimatePresence>
												</Box>

												{/* Navigation */}
												<Box sx={{ mt: 4 }}>
													<Box sx={{ display: "flex", gap: 1.5 }}>
														<Button
															variant="outlined"
															onClick={handlePrev}
															disabled={isFirst}
															sx={{
																minWidth: 52,
																height: 52,
																borderRadius: 2.5,
																p: 0,
																borderColor: theme.palette.divider,
																color: "text.secondary",
																"&:hover": { borderColor: colors.mainSecondary, color: colors.mainSecondary },
																"&.Mui-disabled": { opacity: 0.35 },
															}}>
															<ChevronLeft />
														</Button>

														<Button
															variant="contained"
															onClick={handleNext}
															fullWidth
															endIcon={!isLast && <ChevronRight />}
															sx={{
																height: 52,
																borderRadius: 2.5,
																bgcolor: isLast ? colors.primary : colors.mainSecondary,
																fontFamily: "'Montserrat', sans-serif",
																fontWeight: 700,
																fontSize: "0.9375rem",
																textTransform: "none",
																boxShadow: `0 6px 20px ${alpha(colors.mainSecondary, 0.35)}`,
																"&:hover": {
																	bgcolor: isLast ? "#1a2a8a" : "#17a882",
																	boxShadow: `0 10px 28px ${alpha(colors.mainSecondary, 0.5)}`,
																},
															}}>
															{isLast ? t("howItWorks.getStarted") : t("howItWorks.continue")}
														</Button>
													</Box>

													<Box sx={{ mt: 1.5, textAlign: "center" }}>
														<Typography
															component="button"
															variant="caption"
															color="text.secondary"
															onClick={() => onOpenChange(false)}
															sx={{
																cursor: "pointer",
																border: "none",
																background: "none",
																fontFamily: "'Montserrat', sans-serif",
																"&:hover": { color: colors.primary },
															}}>
															{t("howItWorks.skip")}
														</Typography>
													</Box>
												</Box>
											</Box>
										</Box>
									</CardContent>
								</Card>
							</motion.div>
						</motion.div>
					)}
				</AnimatePresence>
			</Box>
		</Modal>
	);
}
